import React, { useState } from 'react'
import { Card, Form, Input, Select, Button, Table, Tag, Modal, message, Steps, Alert } from 'antd'
import { SendOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { TextArea } = Input
const { Option } = Select

const BatchNotification = () => {
  const [form] = Form.useForm()
  const [step, setStep] = useState(0)
  const [sending, setSending] = useState(false)
  const [tasks, setTasks] = useState([])
  const [previewUsers, setPreviewUsers] = useState([])

  // 加载发送任务
  const loadTasks = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/notifications/tasks`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await res.json()
      if (data.success) setTasks(data.data)
    } catch (err) { console.error(err) }
  }

  React.useEffect(() => { loadTasks() }, [])

  // 预览目标用户
  const previewTargets = async () => {
    const values = form.getFieldsValue()
    if (!values.target) {
      message.warning('请选择发送目标')
      return
    }

    // 模拟预览
    let preview = []
    if (values.target === 'all') preview = [{ phone: '138****0001' }, { phone: '138****0002' }]
    else if (values.target === 'vip') preview = [{ phone: '139****8888' }, { phone: '139****9999' }]
    
    setPreviewUsers(preview)
    setStep(1)
  }

  // 发送通知
  const handleSend = async () => {
    const values = form.getFieldsValue()
    
    setSending(true)
    try {
      const res = await fetch(`${API_BASE}/admin/notifications/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          type: values.type || 'inapp',
          target: values.target,
          title: values.title,
          content: values.content
        })
      })
      const data = await res.json()
      
      if (data.success) {
        message.success(`已开始向 ${data.data.totalCount} 个用户发送通知`)
        setStep(2)
        loadTasks()
      } else {
        message.error(data.error || '发送失败')
      }
    } catch (err) {
      message.error('发送失败')
    }
    setSending(false)
  }

  const taskColumns = [
    { title: '任务ID', dataIndex: 'id', width: 150 },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '类型', dataIndex: 'type', width: 80,
      render: t => <Tag color={t === 'sms' ? 'orange' : t === 'wechat' ? 'green' : 'blue'}>{t}</Tag>
    },
    { title: '进度', width: 200,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            {record.sent_count || 0} / {record.total_count} 
            ({Math.round((record.sent_count / record.total_count) * 100) || 0}%)
          </div>
          <div style={{ width: '100%', height: 4, background: '#f0f0f0', borderRadius: 2 }}>
            <div style={{ 
              width: `${Math.round((record.sent_count / record.total_count) * 100) || 0}%`,
              height: '100%',
              background: '#667eea',
              borderRadius: 2
            }} />
          </div>
        </div>
      )
    },
    { title: '状态', dataIndex: 'status', width: 100,
      render: s => (
        <Tag color={s === 'completed' ? 'green' : s === 'processing' ? 'blue' : 'default'}>
          {s === 'completed' ? '已完成' : s === 'processing' ? '进行中' : '已取消'}
        </Tag>
      )
    },
    { title: '时间', dataIndex: 'created_at', width: 160, render: t => t?.substring(0, 16) }
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>📢 批量发送通知</h2>

      <Steps current={step} style={{ marginBottom: 24 }}>
        <Steps.Step title="填写内容" />
        <Steps.Step title="确认发送" />
        <Steps.Step title="发送完成" />
      </Steps>

      {/* 步骤1：填写内容 */}
      {step === 0 && (
        <Card>
          <Form form={form} layout="vertical">
            <Form.Item label="通知类型" name="type" initialValue="inapp">
              <Select>
                <Option value="inapp">应用内通知</Option>
                <Option value="sms">短信通知</Option>
                <Option value="wechat">微信订阅消息</Option>
              </Select>
            </Form.Item>

            <Form.Item label="发送目标" name="target" rules={[{ required: true }]}>
              <Select placeholder="请选择发送目标">
                <Option value="all">全部用户</Option>
                <Option value="vip">VIP会员</Option>
                <Option value="specific">指定用户</Option>
                <Option value="region">指定地区</Option>
              </Select>
            </Form.Item>

            <Form.Item label="通知标题" name="title" rules={[{ required: true }]}>
              <Input placeholder="请输入通知标题" maxLength={50} />
            </Form.Item>

            <Form.Item label="通知内容" name="content" rules={[{ required: true }]}>
              <TextArea 
                placeholder="请输入通知内容" 
                rows={4} 
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" onClick={previewTargets}>下一步</Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* 步骤2：确认发送 */}
      {step === 1 && (
        <Card>
          <Alert
            message="确认发送通知"
            description={
              <div>
                <p><strong>发送目标：</strong>{form.getFieldValue('target') === 'all' ? '全部用户' : form.getFieldValue('target')}</p>
                <p><strong>标题：</strong>{form.getFieldValue('title')}</p>
                <p><strong>内容：</strong>{form.getFieldValue('content')}</p>
                <p><strong>预计发送：</strong>约 1000 个用户</p>
              </div>
            }
            type="info"
            style={{ marginBottom: 24 }}
          />

          {previewUsers.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <strong>预览用户：</strong>
              <div style={{ marginTop: 8 }}>
                {previewUsers.map((u, i) => (
                  <Tag key={i}>{u.phone}</Tag>
                ))}
                <Tag>...</Tag>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 16 }}>
            <Button onClick={() => setStep(0)}>返回修改</Button>
            <Button type="primary" loading={sending} onClick={handleSend} icon={<SendOutlined />}>
              确认发送
            </Button>
          </div>
        </Card>
      )}

      {/* 步骤3：发送完成 */}
      {step === 2 && (
        <Card>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
            <h2 style={{ marginTop: 16 }}>通知已发送</h2>
            <p style={{ color: '#666' }}>通知正在后台发送中，请稍后刷新查看进度</p>
            <Button type="primary" onClick={() => { setStep(0); form.resetFields() }} style={{ marginTop: 16 }}>
              继续发送
            </Button>
          </div>
        </Card>
      )}

      {/* 发送历史 */}
      <Card title="发送历史" style={{ marginTop: 24 }}>
        <Table
          columns={taskColumns}
          dataSource={tasks}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 5 }}
        />
      </Card>
    </div>
  )
}

export default BatchNotification
